import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import AdminProtectedRoute from './components/auth/AdminProtectedRoute';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ActivatedPacks from './components/lottery/ActivatedPacks';
import DashboardLayout from './components/dashboard/DashboardLayout';
import DashboardGames from './pages/DashboardGames';
import StateGames from './pages/StateGames';
import MissingGames from './pages/MissingGames';
import Audit from './pages/Audit';
import FixOCRPage from './pages/FixOCRNew';
import CreateAuditPage from './pages/CreateAudit';
import StoreSummary from './pages/StoreSummary';
import StoreDetails from './pages/StoreDetails';
import EmailPipeline from './pages/EmailPipeline';
import AccountHolders from './pages/AccountHolders';
import AccountHolderDetails from './pages/AccountHolderDetails';
import RemoteVnc from './pages/RemoteVnc';
import NewAgents from './pages/NewAgents';
import AdminStyleGuideExamples from './pages/AdminStyleGuideExamples';
 
import { OpenCvProvider } from 'opencv-react';
import { MuiTheme } from './utils/theme/muiTheme';


const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard/store-summary" replace />
            ) : (
              <Login />
            )
          }
        />

        {/* Dashboard routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard/store-summary" replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/store-summary/stores/:storeId"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <StoreDetails />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/remote-vnc/:deviceId"
          element={
            <AdminProtectedRoute>
              <RemoteVnc />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/dashboard/new-agents"
          element={
            <AdminProtectedRoute>
              <DashboardLayout>
                <NewAgents />
              </DashboardLayout>
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/dashboard/games"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DashboardGames />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/games/missing"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <MissingGames />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/games/:stateId"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <StateGames />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/store-summary"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <StoreSummary />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin-style-guide"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AdminStyleGuideExamples />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/audit"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Audit />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/audit/create"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <CreateAuditPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/audit/fix-ocr/:queueId"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <FixOCRPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/email-pipeline"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <EmailPipeline />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/account-holders"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AccountHolders />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/account-holders/:holderId"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AccountHolderDetails />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/activated-packs/:storeId"
          element={
            <ProtectedRoute>
              <ActivatedPacks />
            </ProtectedRoute>
          }
        />

        {/* Default redirects */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard/store-summary" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard/store-summary" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider theme={MuiTheme}>
      <OpenCvProvider openCvPath="https://docs.opencv.org/4.x/opencv.js">
        <CssBaseline />
        <AuthProvider>
          <ToastContainer />
          <AppRoutes />
        </AuthProvider>
      </OpenCvProvider>
    </ThemeProvider>
  );
}

export default App;
